import { XMarkIcon } from "@heroicons/react/20/solid";
import { tv } from "tailwind-variants";

import toastIcons from "./toast-icons";
import type { Toast } from "./toast-store";

const toastMessageVariants = tv({
  slots: {
    root: "pointer-events-auto z-50 w-full max-w-sm overflow-hidden rounded-xl border border-line bg-surface-raised shadow-overlay",
    accent: "h-full w-1 shrink-0",
    close: [
      "inline-flex cursor-pointer rounded-md p-0.5 text-content-subtle",
      "transition-colors duration-150 hover:bg-surface-hover hover:text-content",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface-raised focus-visible:outline-none",
    ],
  },
  variants: {
    state: {
      open: { root: "duration-300 animate-in slide-in-from-right" },
      isClosing: { root: "duration-500 animate-out fade-out-0" },
    },
    type: {
      info: { accent: "bg-info" },
      success: { accent: "bg-success" },
      warning: { accent: "bg-warning" },
      error: { accent: "bg-danger" },
    },
  },
});

const { accent, close, root } = toastMessageVariants();

export type ToastMessageProps = {
  toast: Toast;
  onClose: () => void;
};

export const ToastMessage = ({ onClose, toast }: ToastMessageProps) => {
  return (
    <div className={root({ state: toast.state })}>
      <div className="flex items-stretch">
        <div aria-hidden="true" className={accent({ type: toast.type })} />

        <div className="flex flex-1 items-start gap-3 p-4">
          <div className="shrink-0">{toast.icon ?? toastIcons[toast.type]}</div>

          <div className="flex w-0 flex-1 flex-col gap-1 pt-0.5">
            {toast.title ? (
              <p className="text-sm font-semibold text-content-strong">{toast.title}</p>
            ) : null}

            {toast.message ? <p className="text-sm text-content-muted">{toast.message}</p> : null}
          </div>

          <button className={close()} onClick={onClose} type="button">
            <span className="sr-only">Dismiss notification</span>

            <XMarkIcon aria-hidden="true" className="size-5" />
          </button>
        </div>
      </div>
    </div>
  );
};
